import moment from 'moment';
import momentTz, { Moment } from 'moment-timezone';

moment.updateLocale('en', {
  relativeTime: {
    m: '1 minute',
    h: '1 hour',
    d: '1 day',
    w: '1 week',
    M: '1 month',
    y: '1 year',
  },
});

export const DATE_FORMATS = {
  dddd_MMMM_D: 'dddd, MMMM D',
  DD_MMM_YYYY: 'DD-MMM-YYYY',
  M_D_YYYY: 'M/D/YYYY',
  M_D_YYYY_V2: 'M-D-YYYY',
  YYYY_MM_DD_SLASH: 'YYYY/MM/DD',
  MM_DD_YY: 'MM-DD-YY',
  MM_DD_YYYY: 'MM/DD/YYYY',
  MM_DD_YYYY_V2: 'MM-DD-YYYY',
  MMMM_YYYY: 'MMMM YYYY',
  MMM_D: 'MMM D',
  MMM_DO: 'MMM Do',
  UTC_DATE_TIME: 'YYYY-MM-DDTHH:mm:ss.SSSZ', // as presented by the patient-adherence api
  UTC_DATE: 'YYYY-MM-DD',
  YYYY_MM_DD: 'YYYY-MM-DD', // patient adherence birthday format from api
  MMYYYY: 'MMYYYY',
};

export const DEFAULT_TIMEZONE = 'America/New_York';

export const formatDate = (
  date: number | string | Moment | null,
  format?: string | null,
): string => (date ? moment(date).format(format || DATE_FORMATS.M_D_YYYY) : '');

export const formatDateWithTimezone = (
  date: string | Record<string, any> | number | null,
  timezone: string | null,
  format?: string | null,
): string =>
  date
    ? momentTz(date)
        .tz(timezone || DEFAULT_TIMEZONE)
        .format(format || DATE_FORMATS.M_D_YYYY)
    : '';

export const stringToDate = (date: string | null): Date | undefined =>
  date ? moment(date, DATE_FORMATS.UTC_DATE, true).toDate() : undefined;

export const dateWithTimezone = (
  timezoneParam: string | null,
  date?: string | number | Date | null,
  format?: string,
): Moment => {
  const timezone = timezoneParam || DEFAULT_TIMEZONE;
  if (!date) return momentTz().tz(timezone);
  return format
    ? momentTz(date, format, true).tz(timezone)
    : momentTz(date).tz(timezone);
};

export const createMomentWithTimezone = (
  date: string | Date,
  timezone: string,
): Moment => momentTz.tz(date, timezone);

export const extractDateWithoutTz = (date: string, format: string): string =>
  moment.parseZone(date, 'ddd MMM DD YYYY').format(format);

export const convertToSystemTimezone = (momentDate: Moment): Moment => {
  try {
    return moment([momentDate.year(), momentDate.month(), momentDate.date()]);
  } catch (error) {
    return momentDate;
  }
};

//! ====> Consider for DEPRECATION <====
export const createDateWithSystemTimezone = (datetime: Moment | null): Date => {
  return datetime ? convertToSystemTimezone(datetime).toDate() : new Date();
};

export const getCurrentDateOnlyWithTimezone = (timezone: string) => {
  const formattedCurrentDate = moment().format(DATE_FORMATS.YYYY_MM_DD);
  const currentWithTimezone = createMomentWithTimezone(
    formattedCurrentDate,
    timezone,
  );
  return currentWithTimezone;
};

export const isInDateRange = (
  date: Moment,
  startDate: Nullable<Moment>,
  endDate: Moment | null,
): boolean =>
  endDate
    ? date.isBetween(startDate, endDate, undefined, '[]')
    : date.isSameOrAfter(startDate, 'day');

export const calculateSelectionRange = (
  now: string,
  arStartDate?: string,
): { minDate: Date; maxDate: Date } => {
  const minDate = createDateWithSystemTimezone(moment(arStartDate));
  const currentDate = createDateWithSystemTimezone(moment(now));
  return {
    minDate: minDate,
    maxDate: currentDate,
  };
};

/**
 * Shifts a js Date to a given timezone.
 * @param date The date in the current system timezone
 * @param timezone A timezone
 * @return A date that is offset to appear as if it were in the given timezone
 */
export function shiftDate(date: Date, timezone: string): Date {
  const currentTzOffset = moment().utcOffset() * 60 * 1000;
  const desiredTimezoneOffset = moment().tz(timezone).utcOffset() * 60 * 1000;
  const offsetDiff = desiredTimezoneOffset - currentTzOffset;
  return new Date(date.getTime() + offsetDiff);
}

/**
 * Unshifts a js Date from a given timezone.
 * @param date The date in the current system timezone, offset to appear in the given timezone
 * @param timezone A timezone
 * @return The date in the current system timezone
 */
export function unshiftDate(date: Date, timezone: string): Date {
  const currentTzOffset = moment().utcOffset() * 60 * 1000;
  const desiredTimezoneOffset = moment().tz(timezone).utcOffset() * 60 * 1000;
  const offsetDiff = desiredTimezoneOffset - currentTzOffset;
  return new Date(date.getTime() - offsetDiff);
}
